import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription, PlanType, PLANS } from '../contexts/SubscriptionContext';
import PlanCard from '../components/subscription/PlanCard';
import PaymentForm from '../components/subscription/PaymentForm';
import { ShieldCheck, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const Pricing = () => {
  const { isAuthenticated, user } = useAuth();
  const { currentPlan } = useSubscription();
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'year'>('year');
  const navigate = useNavigate();

  const handleSelectPlan = async (planId: PlanType) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    if (planId === 'free') {
      setSelectedPlan(null);
      toast.success('Switched to Free plan successfully');
      return;
    }

    // Stripe integration for paid plans
    if (planId === 'enterprise') {
      const email = user?.email || '';
      // window.open(`https://buy.stripe.com/test_fZueVc4bI9qY5SSb3sdQQ00?prefilled_email=${encodeURIComponent(email)}`, '_blank', 'noopener,noreferrer');
      window.open(`https://buy.stripe.com/6oUfZgdMibz62GG3B0dQQ01?prefilled_email=${encodeURIComponent(email)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (planId === 'standard') {
      const email = user?.email || '';
      window.open(`https://buy.stripe.com/fZueVc4bI9qY5SSb3sdQQ00?prefilled_email=${encodeURIComponent(email)}`, '_blank', 'noopener,noreferrer');
      // window.open(`https://buy.stripe.com/test_6oUfZgdMibz62GG3B0dQQ01?prefilled_email=${encodeURIComponent(email)}`, '_blank', 'noopener,noreferrer');
      return;
    }
  };

  const handlePaymentSuccess = () => {
    setSelectedPlan(null);
    navigate('/dashboard');
  };

  const handlePaymentCancel = () => {
    setSelectedPlan(null);
  };

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="flex justify-center">
            <ShieldCheck className="h-12 w-12 text-primary-500" />
          </div>
          <h1 className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl">
            Pricing Plans
          </h1>
          <p className="mt-4 max-w-xl mx-auto text-xl text-gray-500">
            Choose the perfect plan for your SSL certificate monitoring needs.
          </p>
        </div>

        <div className="mt-12 flex justify-center">
          <div className="relative bg-white rounded-lg p-0.5 flex self-center">
            <button
              type="button"
              className="relative py-2 px-6 border border-transparent rounded-md text-sm font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-primary-500 focus:z-10 bg-primary-500 text-white"
            >
              Yearly billing
            </button>
          </div>
        </div>

        {/* Payment form modal */}
        {selectedPlan && (
          <div className="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    onClick={handlePaymentCancel}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ShieldCheck className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                        Subscribe to {PLANS[selectedPlan].name} Plan
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          ${PLANS[selectedPlan].price}/{billingPeriod}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6">
                  <PaymentForm
                    planId={selectedPlan}
                    onSuccess={handlePaymentSuccess}
                    onCancel={handlePaymentCancel}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="mt-12 space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-x-8">
          <PlanCard
            plan={PLANS.free}
            currentPlanId={currentPlan.id}
            onSelectPlan={handleSelectPlan}
          />
          <PlanCard
            plan={PLANS.standard}
            currentPlanId={currentPlan.id}
            onSelectPlan={handleSelectPlan}
            className="lg:scale-105 lg:-mt-4 lg:-mb-4 lg:shadow-lg"
          />
          <PlanCard
            plan={PLANS.enterprise}
            currentPlanId={currentPlan.id}
            onSelectPlan={handleSelectPlan}
          />
        </div>

        {/* Features comparison */}
        <div className="mt-16">
          <h2 className="text-2xl font-extrabold text-gray-900">
            Compare Plans
          </h2>
          
          <div className="mt-8 overflow-hidden">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Feature
                            </th>
                            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Free
                            </th>
                            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Standard
                            </th>
                            <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Enterprise
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {features.map((feature, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {feature.name}
                              </td>
                              {feature.tiers.map((tier, i) => (
                                <td key={i} className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                  {tier === true ? (
                                    <CheckCircle className="h-5 w-5 text-accent-500 mx-auto" />
                                  ) : tier === false ? (
                                    <span className="text-gray-300">—</span>
                                  ) : (
                                    <span>{tier}</span>
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-2xl font-extrabold text-gray-900">
            Frequently Asked Questions
          </h2>
          
          <div className="mt-8 max-w-3xl mx-auto divide-y-2 divide-gray-200">
            {faqs.map((faq, index) => (
              <div key={index} className="pt-6 pb-8 md:grid md:grid-cols-12 md:gap-8">
                <dt className="text-base font-medium text-gray-900 md:col-span-5">
                  {faq.question}
                </dt>
                <dd className="mt-2 md:mt-0 md:col-span-7">
                  <p className="text-base text-gray-500">{faq.answer}</p>
                </dd>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const features = [
  {
    name: 'Domains',
    tiers: ['3', '15', 'Unlimited']
  },
  {
    name: 'Email Notifications',
    tiers: [true, true, true]
  },
  {
    name: 'SMS Notifications',
    tiers: [false, true, true]
  },
  {
    name: 'Slack Integration',
    tiers: [false, false, true]
  },
  {
    name: 'Private, Internal and Self Generated Certificates',
    tiers: [false, false, true]
  },
  {
    name: 'Teams Integration',
    tiers: [false, false, true]
  },
  // {
  //   name: 'API Access',
  //   tiers: [false, false, true]
  // },
  {
    name: 'Early Alert Period',
    tiers: ['3 days', '15 days', '30 days']
  },
  {
    name: 'Real-time Monitoring',
    tiers: [false, false, true]
  },
  {
    name: 'Support',
    tiers: ['Community', 'Priority', 'Dedicated']
  },
];

const faqs = [
  {
    question: 'How does WeTrackSSL work?',
    answer: 'WeTrackSSL regularly checks your domain SSL certificates and alerts you before they expire, giving you ample time to renew them and avoid website downtime or security warnings for your visitors.'
  },
  {
    question: 'Can I change my plan later?',
    answer: 'Yes! You can upgrade or downgrade your plan at any time. When upgrading, youimmediately gain access to the new features. When downgrading, the change will take effect at the end of your current billing cycle.'
  },
  {
    question: 'Do you offer a free trial?',
    answer: 'We offer a free plan that allows you to monitor up to 3 domains with basic monitoring features. This lets you try out the service before committing to a paid plan.'
  },
  {
    question: 'How are payments handled?',
    answer: 'We process payments securely through Stripe. Your payment information is never stored on our servers, and all transactions are encrypted and secure.'
  },
  {
    question: 'Can I get a refund if Im not satisfied?',
    answer: 'Yes, we offer a 30-day money-back guarantee for all new subscriptions. If you arre not completely satisfied, contact our support team within 30 days of your initial purchase for a full refund.'
  },
];

export default Pricing;